import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

// Constants
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
const API_TIMEOUT = 30000; // 30 seconds
const LOCAL_STORAGE_TOKEN_KEY = 'mocktrail-token';
const LOCAL_STORAGE_REFRESH_TOKEN_KEY = 'mocktrail-refresh-token';

// Error types
export interface ApiError {
  status: number;
  message: string;
  errors?: any;
}

// Generic API response type
export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

// Custom event for auth failures that require logout
export const AUTH_FAILURE_EVENT = 'auth-token-failure';

// Flag to track if a token refresh is in progress
let isRefreshing = false;
// Queue of requests to be retried after token refresh
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: any) => void;
  config: AxiosRequestConfig;
}> = [];

// Check if user is logged out by checking various signals
const checkIfLoggedOut = (): boolean => {
  // If any of these conditions are true, consider the user logged out
  const noToken = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY) === null;
  const noRefreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY) === null;
  const noAuthStorage = localStorage.getItem('mocktrail-auth-storage') === null;
  
  return noToken || (noRefreshToken && noAuthStorage);
};

/**
 * Utility to normalize API responses to a consistent format
 * Handles both formats:
 * 1. Direct: { property1, property2, ... }
 * 2. Nested: { statusCode, message, data: { property1, property2, ... } }
 * 
 * @param response The API response to normalize
 * @returns A normalized response with data always accessible at the top level
 */
export function normalizeApiResponse<T>(response: any): T {
  if (!response) {
    return response;
  }
  
  // If response has the nested structure with statusCode, message, and data
  if (response && typeof response === 'object' && 'statusCode' in response && response.data) {
    // Create a normalized response combining properties from both levels
    return {
      ...response.data, // Primary data at top level 
      _response: { // Keep original metadata nested
        statusCode: response.statusCode,
        message: response.message
      }
    } as unknown as T;
  }
  
  // If already in the expected format, return as is
  return response as T;
}

// Process the request queue based on refresh token result
// FIXED: Now uses the configured axiosInstance instead of global axios to ensure
// proper base URL, interceptors, and configuration are applied to retried requests
const processQueue = (error: any, token: string | null = null, axiosInstance?: AxiosInstance) => {
  console.log(`Processing queue: ${failedQueue.length} requests, error: ${error ? 'yes' : 'no'}, token: ${token ? 'yes' : 'no'}, axiosInstance: ${axiosInstance ? 'yes' : 'no'}`);
  
  failedQueue.forEach((request, index) => {
    if (error) {
      console.log(`Rejecting queued request ${index + 1} due to error:`, error.message);
      request.reject(error);
    } else if (token && axiosInstance) {
      // Retry the request with new token using the configured axios instance
      console.log(`Retrying queued request ${index + 1} with new token`);
      try {
        if (request.config.headers) {
          request.config.headers.Authorization = `Bearer ${token}`;
        }
        // Use the configured axios instance instead of global axios
        const retryPromise = axiosInstance(request.config);
        request.resolve(retryPromise);
      } catch (retryError) {
        console.error(`Error retrying queued request ${index + 1}:`, retryError);
        request.reject(retryError);
      }
    } else {
      console.error(`Cannot process queued request ${index + 1}: missing token or axiosInstance`);
      request.reject(new Error('Failed to retry request: missing token or axios instance'));
    }
  });
  
  // Reset the queue
  failedQueue = [];
};

// Function to validate token format and expiration
export const validateToken = (token: string): boolean => {
  if (!token || !token.match(/^[A-Za-z0-9-_]+\.[A-Za-z0-9-_]+\.[A-Za-z0-9-_]*$/)) {
    return false;
  }
  
  try {
    const tokenParts = token.split('.');
    const payload = JSON.parse(atob(tokenParts[1]));
    
    if (payload.exp) {
      const expirationTime = payload.exp * 1000;
      const currentTime = Date.now();
      
      // Add a buffer of 10 seconds to avoid edge cases
      if (currentTime > (expirationTime - 10000)) {
        return false;
      }
    }
    
    return true;
  } catch (error) {
    console.error("Error validating token:", error);
    return false;
  }
};

// Helper function to properly handle auth failures
const handleAuthFailure = (reason: string) => {
  console.warn(`Auth failure: ${reason}`);
  
  // Dispatch a custom event for components to handle logout
  const authFailureEvent = new CustomEvent(AUTH_FAILURE_EVENT, { 
    detail: { reason } 
  });
  window.dispatchEvent(authFailureEvent);
};

// Base API client class
class ApiClient {
  private static instance: ApiClient;
  private axiosInstance: AxiosInstance;
  private token: string | null = null;

  private constructor() {
    this.axiosInstance = axios.create({
      baseURL: API_BASE_URL,
      timeout: API_TIMEOUT,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Only add Authorization header if authRequired is true or not specified
        const authRequired = config.headers?.['X-Auth-Required'] !== 'false';
        
        // Remove our custom header so it doesn't get sent to the server
        if ('X-Auth-Required' in (config.headers || {})) {
          delete config.headers?.['X-Auth-Required'];
        }
        
        // Add token only if auth is required
        if (authRequired) {
          // Get token and validate it
          const token = this.token || localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
          
          if (token && validateToken(token)) {
            config.headers.Authorization = `Bearer ${token}`;
            this.token = token; // Ensure token is set in the instance
          }
        }
        
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Use our normalization utility for consistent response handling
        return normalizeApiResponse(response.data);
      },
      async (error) => {
        const originalRequest = error.config;
        
        // Format API errors
        const apiError: ApiError = {
          status: error.response?.status || 500,
          message: error.response?.data?.message || 'An unexpected error occurred',
          errors: error.response?.data?.errors,
        };

        // Only handle 401 errors if auth was required
        const authRequired = originalRequest.headers?.['X-Auth-Required'] !== 'false';
        
        // Handle token expiration (401 errors)
        if (authRequired && apiError.status === 401 && !originalRequest._retry) {
          // Check the URL - if this is already a refresh token request, handle it differently
          // This prevents a loop of refresh token attempts
          const isRefreshTokenRequest = originalRequest.url && 
                                       originalRequest.url.includes('/auth/refresh');
          
          if (isRefreshTokenRequest) {
            console.log('Refresh token request itself returned 401 - token is invalid');
            this.clearToken();
            handleAuthFailure('refresh_token_invalid');
            return Promise.reject(apiError);
          }
          
          // First check if user is already logged out to avoid unnecessary refresh attempts
          if (checkIfLoggedOut()) {
            console.log('Token refresh aborted: User is already logged out');
            // Dispatch auth failure event to ensure consistency
            handleAuthFailure('user_logged_out');
            return Promise.reject(apiError);
          }
          
          if (isRefreshing) {
            // If a refresh is already in progress, queue this request
            console.log('Token refresh already in progress, queueing request');
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject, config: originalRequest });
            });
          }
          
          originalRequest._retry = true;
          isRefreshing = true;
          
          try {
            // Get refresh token
            const refreshToken = localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
            
            if (!refreshToken) {
              throw new Error('No refresh token available');
            }
            
            console.log('Token expired, attempting to refresh using refresh token');
            
            // Attempt to refresh the token
            try {
              // Use a separate try/catch to specifically handle refresh token 401 responses
              const response = await axios.post(`${API_BASE_URL}/auth/refresh`, 
                { refresh_token: refreshToken },
                { 
                  baseURL: API_BASE_URL,
                  headers: { 'Content-Type': 'application/json' }
                }
              );
              
              console.log('Refresh token response received:', response.status);
              
              // Check again if user logged out during the refresh request
              if (checkIfLoggedOut()) {
                console.log('User logged out during token refresh, aborting');
                processQueue(new Error('User logged out during refresh'), null, this.axiosInstance);
                handleAuthFailure('user_logged_out');
                return Promise.reject(apiError);
              }
              
              // Extract token from response
              let newToken = null;
              if (response.data && response.data.data) {
                newToken = response.data.data.access_token;
              } else if (response.data) {
                newToken = response.data.access_token;
              }
              
              if (newToken) {
                console.log('Token refreshed successfully');
                
                // Update token in storage and instance
                this.setToken(newToken);
                
                // Also store refresh token if provided
                if (response.data.data?.refresh_token || response.data.refresh_token) {
                  const newRefreshToken = response.data.data?.refresh_token || response.data.refresh_token;
                  localStorage.setItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY, newRefreshToken);
                }
                
                // Update authorization header for the original request
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                
                // Process any queued requests with the new token
                processQueue(null, newToken, this.axiosInstance);
                
                // Add a small delay to ensure token is properly set before retrying
                await new Promise(resolve => setTimeout(resolve, 50));
                
                // Retry the original request
                return this.axiosInstance(originalRequest);
              } else {
                // If no token in response, handle as authentication failure
                console.error('Refresh token response did not contain a new token');
                this.clearToken();
                processQueue(new Error('Failed to refresh token'), null, this.axiosInstance);
                
                // Trigger auth failure event instead of direct redirect
                handleAuthFailure('refresh_token_invalid');
                
                return Promise.reject(apiError);
              }
            } catch (refreshError: any) {
              // Check specifically for 401 from refresh token endpoint
              if (refreshError.response && refreshError.response.status === 401) {
                console.error('Refresh token was rejected with 401 Unauthorized');
                
                // This is a case where we should definitely logout - refresh token is invalid
                this.clearToken();
                processQueue(new Error('Refresh token rejected with 401'), null, this.axiosInstance);
                
                // Trigger auth failure event to initiate logout
                handleAuthFailure('refresh_token_invalid');
                
                return Promise.reject(apiError);
              }
              
              // For other refresh token errors
              console.error('Error refreshing token:', refreshError);
              this.clearToken();
              processQueue(refreshError, null, this.axiosInstance);
              
              // Trigger auth failure event instead of direct redirect
              handleAuthFailure('refresh_token_error');
              
              return Promise.reject(apiError);
            }
          } catch (refreshError) {
            // Refresh token request failed
            console.error('Error refreshing token:', refreshError);
            this.clearToken();
            processQueue(refreshError, null, this.axiosInstance);
            
            // Trigger auth failure event instead of direct redirect
            handleAuthFailure('refresh_token_error');
            
            return Promise.reject(apiError);
          } finally {
            isRefreshing = false;
          }
        }

        return Promise.reject(apiError);
      }
    );
  }

  // Singleton pattern
  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  // Set auth token
  public setToken(token: string): void {
    this.token = token;
    localStorage.setItem(LOCAL_STORAGE_TOKEN_KEY, token);
  }

  // Clear auth token
  public clearToken(): void {
    this.token = null;
    localStorage.removeItem(LOCAL_STORAGE_TOKEN_KEY);
    localStorage.removeItem(LOCAL_STORAGE_REFRESH_TOKEN_KEY);
  }

  // Load token from storage
  public loadToken(): void {
    const token = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
    if (token) {
      this.token = token;
    }
  }

  // HTTP methods with authRequired parameter
  public async get<T>(url: string, config?: AxiosRequestConfig, authRequired: boolean = true): Promise<T> {
    try {
      console.log(`API Client: Making GET request to: ${API_BASE_URL}${url}`); // With base url
      const requestConfig = {
        ...config,
        headers: {
          ...config?.headers, 
          'X-Auth-Required': authRequired.toString()
        }
      };
      console.log('API Client: Request config:', JSON.stringify(requestConfig));
      const response = await this.axiosInstance.get<T>(url, requestConfig);
      console.log(`API Client: GET response for ${url}:`, response);
      return response as unknown as T;
    } catch (error) {
      console.error(`API Client: Error in GET request to ${url}:`, error);
      throw error;
    }
  }

  public async post<T>(url: string, data?: any, config?: AxiosRequestConfig, authRequired: boolean = true): Promise<T> {
    const requestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'X-Auth-Required': authRequired.toString()
      }
    };
    const response = await this.axiosInstance.post<T>(url, data, requestConfig);
    return response as unknown as T;
  }

  public async put<T>(url: string, data?: any, config?: AxiosRequestConfig, authRequired: boolean = true): Promise<T> {
    const requestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'X-Auth-Required': authRequired.toString()
      }
    };
    const response = await this.axiosInstance.put<T>(url, data, requestConfig);
    return response as unknown as T;
  }

  public async patch<T>(url: string, data?: any, config?: AxiosRequestConfig, authRequired: boolean = true): Promise<T> {
    const requestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'X-Auth-Required': authRequired.toString()
      }
    };
    const response = await this.axiosInstance.patch<T>(url, data, requestConfig);
    return response as unknown as T;
  }

  public async delete<T>(url: string, config?: AxiosRequestConfig, authRequired: boolean = true): Promise<T> {
    const requestConfig = {
      ...config,
      headers: {
        ...config?.headers,
        'X-Auth-Required': authRequired.toString()
      }
    };
    const response = await this.axiosInstance.delete<T>(url, requestConfig);
    return response as unknown as T;
  }

  /**
   * Make a request to the jobprot-api with proper authorization
   * This is a specialized method for the jobprot backend API that handles authorization properly
   * 
   * @param endpoint The API endpoint path (without the /jobprot-api prefix)
   * @param method The HTTP method (GET, POST, PUT, DELETE)
   * @param data The request payload for POST, PUT requests
   * @param authRequired Whether authorization is required for this request
   * @returns The API response
   */
  public async jobprotRequest<T>(
    endpoint: string, 
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', 
    data?: any,
    authRequired: boolean = true
  ): Promise<T> {
    // Only check for token if auth is required
    if (authRequired) {
      const token = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
      
      if (!token) {
        console.error('No token available for jobprot API request that requires authentication');
        throw new Error('Authentication required');
      }
    }
    
    // Use axios directly to ensure proper headers and URL
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
    const url = `${baseUrl}/jobprot-api${endpoint.startsWith('/') ? endpoint : '/' + endpoint}`;
    
    console.log(`Making ${method} request to jobprot API: ${url} (Auth Required: ${authRequired})`);
    
    try {
      // Build headers based on whether auth is required and data type
      const headers: Record<string, string> = {
        'Accept': 'application/json'
      };
      
      // Only set Content-Type if it's not FormData (browser will set it automatically for FormData)
      if (!(data instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
      }
      
      // Only add Authorization header if required
      if (authRequired) {
        const token = localStorage.getItem(LOCAL_STORAGE_TOKEN_KEY);
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      }
      
      const response = await axios({
        method,
        url,
        data: method !== 'GET' ? data : undefined,
        headers
      });
      
      return response.data;
    } catch (error) {
      console.error('Error in jobprot API request:', error);
      throw error;
    }
  }
}

// Export the singleton instance
const apiClient = ApiClient.getInstance();
export default apiClient; 